import { useCallback, useState } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { SidebarRightOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, Drawer, IconButton, Stack, Tooltip, useSnackbar } from '@lyearn/ui';
import { ChatFragment } from '@/features/messages/graphql/fragments/generated/Chat';

import { useCreateNoteMutation } from '../../graphql/mutations/generated/createNote';
import { NoteFragment } from '../../graphql/queries/generated/notes';
import { height100vh, height100vhMinusHeader } from '../../styles';
import { PinnedContentType, ViewMode } from '../../types';
import { Chats } from '../Chats';
import { CreateNewHoverPopover } from '../CreateNewHoverPopover';
import { KnowledgeBaseHeader } from '../KnowledgeBaseHeader';
import { KnowledgeBaseSearch } from '../KnowledgeBaseSearch';

export const KnowledgeBaseSidebar = ({
  currentChannel,
  currentNote,
  currentContent,
  onNewChat,
  setCurrentChannel,
  setChannelTitle,
  setCurrentNote,
  setCurrentContent,
  setViewMode,
  setUpdatedTitle,
  setIsFreshChat,
  theGreatReset,
  showListDrawer,
  setShowListDrawer,
  teachModeEnabled,
}: {
  currentChannel: ChatFragment | null;
  currentNote: NoteFragment | null;
  currentContent: PinnedContentType | null;
  onNewChat: () => void;
  setCurrentChannel: React.Dispatch<React.SetStateAction<ChatFragment | null>>;
  setChannelTitle: React.Dispatch<React.SetStateAction<string>>;
  setCurrentNote: React.Dispatch<React.SetStateAction<NoteFragment | null>>;
  setViewMode: React.Dispatch<React.SetStateAction<ViewMode>>;
  setUpdatedTitle: React.Dispatch<React.SetStateAction<string | undefined>>;
  setCurrentContent: React.Dispatch<React.SetStateAction<PinnedContentType | null>>;
  setIsFreshChat: React.Dispatch<React.SetStateAction<boolean>>;
  theGreatReset: () => void;
  showListDrawer: boolean;
  setShowListDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  teachModeEnabled: boolean;
}) => {
  const { isMobile } = useScreen();
  const { t } = useTranslation(['Common']);
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const [_, createNote] = useCreateNoteMutation();
  const [search, setSearch] = useState('');
  const [isCollapsed, { toggle: onToggle }] = useToggle(isMobile ? true : false);

  const onNewNote = useCallback(() => {
    createNote({
      input: {
        title: t('Common:KnowledgeBase.UntitledNote'),
      },
    })
      .then((res) => {
        if (res?.data?.createNote?.note?.id?.length) {
          setCurrentNote({
            ...res?.data?.createNote?.note,
            title: t('Common:KnowledgeBase.UntitledNote'),
          });
          setViewMode(ViewMode.Notes);
          showSnackbar({ message: t('Common:KnowledgeBase.NoteCreated') });
        } else {
          showErrorSnackbar({ message: t('Common:Error.SomethingWentWrong') });
        }
      })
      .catch(() => {
        showErrorSnackbar({ message: t('Common:Error.SomethingWentWrong') });
      });
  }, [createNote, setCurrentNote, setViewMode, showErrorSnackbar, showSnackbar, t]);

  if (isCollapsed && !isMobile) {
    return (
      <Box className="relative">
        <Stack
          className="fixed top-[var(--ly-header-height,0px)] z-[50] translate-x-[1.2rem]"
          direction="col"
          gapY={8}>
          <Tooltip placement="right" title="Show Sidebar">
            <IconButton Icon={SidebarRightOutline} size="xl" variant="shadow" onClick={onToggle} />
          </Tooltip>
          <CreateNewHoverPopover onNewChat={onNewChat} onNewNote={onNewNote} />
        </Stack>
      </Box>
    );
  }

  const renderSidebar = () => {
    return (
      <Box
        className={cn(
          isMobile ? height100vh : height100vhMinusHeader,
          'min-w-[30rem] max-w-[30rem] bg-bg-secondary relative',
        )}>
        <Stack
          className={cn(
            isMobile ? height100vh : height100vhMinusHeader,
            'p-16 pt-10 w-full overflow-y-auto',
          )}
          direction="col"
          gapY={16}>
          <KnowledgeBaseHeader
            hideCollapseButton={isMobile}
            plusIconRight={isMobile}
            onNew={onNewChat}
            onToggleCollapsed={onToggle}
          />

          <KnowledgeBaseSearch search={search} setSearch={setSearch} />

          <Stack className="mt-12" direction="col" gapY={12}>
            <Chats
              currentChannel={currentChannel}
              search={search}
              setChannelTitle={setChannelTitle}
              setCurrentChannel={setCurrentChannel}
              setIsFreshChat={setIsFreshChat}
              setShowListDrawer={setShowListDrawer}
              setViewMode={setViewMode}
              teachModeEnabled={teachModeEnabled}
              theGreatReset={theGreatReset}
              onNewChat={onNewChat}
            />

            {/* <div className={cn(horizontalDivider, 'my-12 bg-interactive-secondary-hover')} />
            <PinnedItems
              currentContent={currentContent}
              search={search}
              setCurrentContent={setCurrentContent}
              setUpdatedTitle={setUpdatedTitle}
              setViewMode={setViewMode}
              theGreatReset={theGreatReset}
            /> */}
          </Stack>
        </Stack>
      </Box>
    );
  };

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        classes={{ paper: cn(height100vh) }}
        open={showListDrawer}
        onClose={() => setShowListDrawer(false)}>
        {renderSidebar()}
      </Drawer>
    );
  }

  return isMobile ? null : renderSidebar();
};
